import React, { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../utils/apiClient';
import { API_BASE_URL } from '../constants/api';
import { useAuth } from '../context/AuthContext';
import { ROLE_IDS, hasEffectivePermission } from '../utils/accessControl';
import AppIcon from '../components/AppIcon';
import '../styles/Nonconformities.css';

const API_BASE = API_BASE_URL;

const CATEGORY_OPTIONS = [
  { value: 'quality', label: 'Kalite' },
  { value: 'safety', label: 'İş Güvenliği' },
];

const SEVERITY_OPTIONS = [
  { value: 'low', label: 'Düşük' },
  { value: 'medium', label: 'Orta' },
  { value: 'high', label: 'Yüksek' },
  { value: 'critical', label: 'Kritik' },
];

const STATUS_OPTIONS = [
  { value: 'open', label: 'Açık' },
  { value: 'in_progress', label: 'İşlemde' },
  { value: 'closed', label: 'Kapalı' },
];

const INITIAL_FORM = {
  project_id: '',
  task_id: '',
  category: 'quality',
  title: '',
  description: '',
  severity: 'medium',
  status: 'open',
  sla_due_date: '',
};

const EMPTY_INSIGHTS = {
  lookback_days: 90,
  recurring_findings: [],
  project_hotspots: [],
  severity_breakdown: [],
};

const formatShortDate = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleDateString('tr-TR');
};

const Nonconformities = () => {
  const { user, permissions } = useAuth();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [insights, setInsights] = useState(EMPTY_INSIGHTS);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [filters, setFilters] = useState({ project_id: 'all', status: 'all', category: 'all' });
  const [showForm, setShowForm] = useState(false);
  const [formState, setFormState] = useState(INITIAL_FORM);
  const [editingId, setEditingId] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showInfo, setShowInfo] = useState(false);
  const canManageNonconformities =
    [ROLE_IDS.ADMIN, ROLE_IDS.PROJECT_MANAGER, ROLE_IDS.SITE_SUPERVISOR, ROLE_IDS.ENGINEER].includes(Number(user?.role_id)) &&
    hasEffectivePermission(user, permissions, 'can_manage_change_orders');

  const fetchProjects = useCallback(async () => {
    try {
      const response = await apiClient.get(`${API_BASE}/projects`);
      setProjects(Array.isArray(response.data) ? response.data : []);
    } catch (requestError) {
      setProjects([]);
    }
  }, []);

  const fetchTasks = useCallback(async (projectIdValue) => {
    const numericProjectId = Number(projectIdValue || 0);
    if (!numericProjectId) {
      setTasks([]);
      return;
    }
    try {
      const response = await apiClient.get(`${API_BASE}/tasks/project/${numericProjectId}`);
      setTasks(Array.isArray(response.data) ? response.data : []);
    } catch (requestError) {
      setTasks([]);
    }
  }, []);

  const fetchRows = useCallback(async () => {
    try {
      setLoading(true);
      const query = new URLSearchParams();
      if (filters.project_id !== 'all') query.set('project_id', filters.project_id);
      if (filters.status !== 'all') query.set('status', filters.status);
      if (filters.category !== 'all') query.set('category', filters.category);

      const [listResponse, summaryResponse, insightsResponse] = await Promise.all([
        apiClient.get(`${API_BASE}/nonconformities?${query.toString()}`),
        apiClient.get(
          `${API_BASE}/nonconformities/summary${filters.project_id !== 'all' ? `?project_id=${filters.project_id}` : ''}`
        ),
        apiClient.get(
          `${API_BASE}/nonconformities/insights${filters.project_id !== 'all' ? `?project_id=${filters.project_id}` : ''}`
        ),
      ]);
      setRows(Array.isArray(listResponse.data) ? listResponse.data : []);
      setSummary(summaryResponse.data || {});
      setInsights({
        ...EMPTY_INSIGHTS,
        ...(insightsResponse.data || {}),
      });
      setError('');
    } catch (requestError) {
      setRows([]);
      setSummary({});
      setInsights(EMPTY_INSIGHTS);
      setError(requestError.response?.data?.message || 'Uygunsuzluk kayıtları yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  useEffect(() => {
    const sourceProjectId = formState.project_id || (filters.project_id !== 'all' ? filters.project_id : '');
    if (sourceProjectId) {
      fetchTasks(sourceProjectId);
    } else {
      setTasks([]);
    }
  }, [fetchTasks, filters.project_id, formState.project_id]);

  const openCreateForm = () => {
    if (!canManageNonconformities) return;
    setEditingId(0);
    setFormState({
      ...INITIAL_FORM,
      project_id: filters.project_id !== 'all' ? String(filters.project_id) : '',
    });
    setShowForm(true);
  };

  const openEditForm = (row) => {
    if (!canManageNonconformities) return;
    setEditingId(Number(row.id || 0));
    setFormState({
      project_id: String(row.project_id || ''),
      task_id: row.task_id ? String(row.task_id) : '',
      category: row.category || 'quality',
      title: row.title || '',
      description: row.description || '',
      severity: row.severity || 'medium',
      status: row.status || 'open',
      sla_due_date: row.sla_due_date ? String(row.sla_due_date).slice(0, 10) : '',
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(0);
    setFormState(INITIAL_FORM);
  };

  const saveForm = async () => {
    if (!canManageNonconformities) return;
    if (!formState.project_id || !String(formState.title || '').trim()) {
      setError('Proje ve başlık zorunludur.');
      return;
    }

    try {
      const payload = {
        project_id: Number(formState.project_id),
        task_id: formState.task_id ? Number(formState.task_id) : null,
        category: formState.category,
        title: String(formState.title || '').trim(),
        description: String(formState.description || '').trim() || null,
        severity: formState.severity,
        status: formState.status,
        sla_due_date: formState.sla_due_date || null,
      };

      if (editingId > 0) {
        await apiClient.put(`${API_BASE}/nonconformities/${editingId}`, payload);
      } else {
        await apiClient.post(`${API_BASE}/nonconformities`, payload);
      }

      await fetchRows();
      closeForm();
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Kayıt işlemi başarısız.');
    }
  };

  const quickSetStatus = async (id, nextStatus) => {
    if (!canManageNonconformities) return;
    try {
      await apiClient.put(`${API_BASE}/nonconformities/${id}`, { status: nextStatus });
      await fetchRows();
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Durum güncellenemedi.');
    }
  };

  const removeRow = async (id) => {
    if (!canManageNonconformities) return;
    if (!window.confirm('Uygunsuzluk kaydı silinsin mi?')) return;
    try {
      await apiClient.delete(`${API_BASE}/nonconformities/${id}`);
      await fetchRows();
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Silme işlemi başarısız.');
    }
  };

  return (
    <div className="nonconformities-page">
      <div className="nonconformities-card">
        <div className="nonconformities-head">
          <div>
            <div className="module-title-row">
              <h1>Uygunsuzluk Yönetimi</h1>
              <button
                type="button"
                className={`module-info-btn ${showInfo ? 'active' : ''}`}
                onClick={() => setShowInfo((prev) => !prev)}
                title="Bu modül ne işe yarar?"
              >
                <AppIcon name="info" className="module-info-icon" />
              </button>
            </div>
            {showInfo && (
              <div className="module-info-card">
                <p>
                  Uygunsuzluk kaydı; sahada tespit edilen kalite veya iş güvenliği problemlerini
                  <strong> kanıt, önem seviyesi, SLA tarihi ve kapanış durumu</strong> ile izler.
                </p>
                <p>
                  Amaç, problemi kayıt altına almak, sorumlu aksiyonu takip etmek ve tekrar eden
                  hataları görünür hale getirerek önleyici iyileştirme yapmaktır.
                </p>
              </div>
            )}
          </div>
          {canManageNonconformities && (
            <button type="button" className="btn-primary" onClick={openCreateForm}>
              Yeni Uygunsuzluk
            </button>
          )}
        </div>

        <div className="nonconformities-summary">
          <div className="summary-box"><span>Toplam</span><strong>{Number(summary.total_count || 0)}</strong></div>
          <div className="summary-box"><span>Açık</span><strong>{Number(summary.open_count || 0)}</strong></div>
          <div className="summary-box"><span>İşlemde</span><strong>{Number(summary.in_progress_count || 0)}</strong></div>
          <div className="summary-box"><span>Kapalı</span><strong>{Number(summary.closed_count || 0)}</strong></div>
          <div className="summary-box"><span>SLA Gecikenler</span><strong>{Number(summary.overdue_sla_count || 0)}</strong></div>
        </div>

        <div className="nonconformities-insights">
          <div className="insight-card">
            <div className="insight-title">Tekrarlayan Bulgular (Son {Number(insights.lookback_days || 90)} Gün)</div>
            {Array.isArray(insights.recurring_findings) && insights.recurring_findings.length > 0 ? (
              insights.recurring_findings.slice(0, 5).map((item, index) => (
                <div className="insight-row" key={`${item.title}-${index}`}>
                  <div>
                    <strong>{item.title}</strong>
                    <small>{item.category === 'safety' ? 'İş Güvenliği' : 'Kalite'} · Son: {formatShortDate(item.last_seen_at)}</small>
                  </div>
                  <div className="insight-badges">
                    <span>{Number(item.total_count || 0)} tekrar</span>
                    <span className="alert">{Number(item.open_count || 0)} açık</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="insight-empty">Tekrarlayan bulgu görünmüyor.</div>
            )}
          </div>

          <div className="insight-card">
            <div className="insight-title">Proje Bazlı Yoğunluk</div>
            {Array.isArray(insights.project_hotspots) && insights.project_hotspots.length > 0 ? (
              insights.project_hotspots.slice(0, 5).map((item, index) => (
                <div className="insight-row" key={`${item.project_id}-${index}`}>
                  <div>
                    <strong>{item.project_name || `Proje #${item.project_id}`}</strong>
                    <small>Toplam kayıt: {Number(item.total_count || 0)}</small>
                  </div>
                  <div className="insight-badges">
                    <span className="alert">{Number(item.open_count || 0)} açık</span>
                    <span className="danger">{Number(item.critical_count || 0)} kritik</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="insight-empty">Yoğunluk verisi bulunamadı.</div>
            )}
          </div>

          <div className="insight-card">
            <div className="insight-title">Önem Dağılımı</div>
            {Array.isArray(insights.severity_breakdown) && insights.severity_breakdown.length > 0 ? (
              insights.severity_breakdown.map((item, index) => (
                <div className="insight-row" key={`${item.severity}-${index}`}>
                  <div>
                    <strong>{SEVERITY_OPTIONS.find((entry) => entry.value === item.severity)?.label || item.severity}</strong>
                    <small>Toplam: {Number(item.total_count || 0)}</small>
                  </div>
                  <div className="insight-badges">
                    <span className="alert">{Number(item.open_count || 0)} açık</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="insight-empty">Önem kırılımı bulunamadı.</div>
            )}
          </div>
        </div>

        <div className="nonconformities-filters">
          <select
            value={filters.project_id}
            onChange={(event) => setFilters((prev) => ({ ...prev, project_id: event.target.value }))}
          >
            <option value="all">Tüm Projeler</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>{project.name}</option>
            ))}
          </select>
          <select
            value={filters.status}
            onChange={(event) => setFilters((prev) => ({ ...prev, status: event.target.value }))}
          >
            <option value="all">Tüm Durumlar</option>
            {STATUS_OPTIONS.map((item) => (
              <option key={item.value} value={item.value}>{item.label}</option>
            ))}
          </select>
          <select
            value={filters.category}
            onChange={(event) => setFilters((prev) => ({ ...prev, category: event.target.value }))}
          >
            <option value="all">Tüm Kategoriler</option>
            {CATEGORY_OPTIONS.map((item) => (
              <option key={item.value} value={item.value}>{item.label}</option>
            ))}
          </select>
        </div>

        {error && <div className="error-banner">{error}</div>}

        {loading ? (
          <div className="loading-banner">Yükleniyor...</div>
        ) : rows.length === 0 ? (
          <div className="empty-banner">Kayıt bulunamadı.</div>
        ) : (
          <div className="nonconformities-table-wrap">
            <table className="nonconformities-table">
              <thead>
                <tr>
                  <th>Kayıt</th>
                  <th>Proje</th>
                  <th>Kategori</th>
                  <th>Önem</th>
                  <th>Durum</th>
                  <th>SLA</th>
                  <th>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <strong>{row.title}</strong>
                      <div className="row-subtext">{row.description || '-'}</div>
                    </td>
                    <td>{row.project_name || row.project_id}</td>
                    <td>{row.category === 'safety' ? 'İş Güvenliği' : 'Kalite'}</td>
                    <td>{SEVERITY_OPTIONS.find((item) => item.value === row.severity)?.label || row.severity}</td>
                    <td>{STATUS_OPTIONS.find((item) => item.value === row.status)?.label || row.status}</td>
                    <td>{row.sla_due_date ? String(row.sla_due_date).slice(0, 10) : '-'}</td>
                    <td>
                      <div className="row-actions">
                        {canManageNonconformities && (
                          <>
                            <button type="button" className="btn-ghost" onClick={() => openEditForm(row)}>
                              Düzenle
                            </button>
                            {row.status !== 'closed' && (
                              <button type="button" className="btn-ghost approve" onClick={() => quickSetStatus(row.id, 'closed')}>
                                Kapat
                              </button>
                            )}
                            {row.status === 'open' && (
                              <button type="button" className="btn-ghost" onClick={() => quickSetStatus(row.id, 'in_progress')}>
                                İşleme Al
                              </button>
                            )}
                            <button type="button" className="btn-ghost danger" onClick={() => removeRow(row.id)}>
                              Sil
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && (
        <div className="modal-overlay" onClick={closeForm}>
          <div className="modal-card" onClick={(event) => event.stopPropagation()}>
            <div className="modal-head">
              <h2>{editingId > 0 ? 'Uygunsuzluk Düzenle' : 'Yeni Uygunsuzluk'}</h2>
              <button type="button" className="btn-close" onClick={closeForm}>X</button>
            </div>

            <div className="modal-grid">
              <label>
                Proje
                <select
                  value={formState.project_id}
                  onChange={(event) => setFormState((prev) => ({ ...prev, project_id: event.target.value, task_id: '' }))}
                >
                  <option value="">Seçiniz</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>{project.name}</option>
                  ))}
                </select>
              </label>

              <label>
                İlgili Görev
                <select
                  value={formState.task_id}
                  onChange={(event) => setFormState((prev) => ({ ...prev, task_id: event.target.value }))}
                >
                  <option value="">Seçiniz</option>
                  {tasks.map((task) => (
                    <option key={task.id} value={task.id}>{task.title}</option>
                  ))}
                </select>
              </label>

              <label>
                Kategori
                <select
                  value={formState.category}
                  onChange={(event) => setFormState((prev) => ({ ...prev, category: event.target.value }))}
                >
                  {CATEGORY_OPTIONS.map((item) => (
                    <option key={item.value} value={item.value}>{item.label}</option>
                  ))}
                </select>
              </label>

              <label>
                Önem Seviyesi
                <select
                  value={formState.severity}
                  onChange={(event) => setFormState((prev) => ({ ...prev, severity: event.target.value }))}
                >
                  {SEVERITY_OPTIONS.map((item) => (
                    <option key={item.value} value={item.value}>{item.label}</option>
                  ))}
                </select>
              </label>

              <label className="full-row">
                Başlık
                <input
                  type="text"
                  value={formState.title}
                  onChange={(event) => setFormState((prev) => ({ ...prev, title: event.target.value }))}
                />
              </label>

              <label className="full-row">
                Açıklama
                <textarea
                  rows="3"
                  value={formState.description}
                  onChange={(event) => setFormState((prev) => ({ ...prev, description: event.target.value }))}
                />
              </label>

              <label>
                Durum
                <select
                  value={formState.status}
                  onChange={(event) => setFormState((prev) => ({ ...prev, status: event.target.value }))}
                >
                  {STATUS_OPTIONS.map((item) => (
                    <option key={item.value} value={item.value}>{item.label}</option>
                  ))}
                </select>
              </label>

              <label>
                SLA Tarihi
                <input
                  type="date"
                  value={formState.sla_due_date}
                  onChange={(event) => setFormState((prev) => ({ ...prev, sla_due_date: event.target.value }))}
                />
              </label>
            </div>

            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={closeForm}>İptal</button>
              {canManageNonconformities && (
              <button type="button" className="btn-primary" onClick={saveForm}>Kaydet</button>
            )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Nonconformities;




